import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "wouter";
import ShippingAddresses from "./shippingAddresses";
import {
  ConfigProvider,
  Input,
  Select,
  Typography,
  Button,
  Radio,
  Spin,
} from "antd";
import { PDetailsPrice } from "../../../redux/actions/adminActions/newOrderRequestAction";
import { AllProductsAPI } from "../../../redux/actions/adminActions/allProductsAction";
import { CloseSquareOutlined, LoadingOutlined } from "@ant-design/icons";
import { logout } from "../../../redux/reducers/authSlice";
import OrderDetailsModal from "./orderDetailsModal";
import {
  DealerSaveToCart,
  GetDealerCart,
} from "../../../redux/actions/userActions/ordersActions";
import ChangeDealerCode from "../../components/common/changeDealerCode";

const { Title } = Typography;

const Cart = () => {
  const authData = useSelector((state) => state.auth_store);
  const dealer_code = authData?.defaultDealerCode;

  const allProducts = useSelector((state) => state.all_product);
  const all_products_options = useMemo(
    () => allProducts.all_products_slice || [],
    [allProducts]
  );

  const dispatch = useDispatch();
  const [, setLocation] = useLocation();
  const [balanceCredit, setBalanceCredit] = useState(0);
  const [defaultShippingAddress, setDefaultShippingAddress] = useState("");
  const [shipmentType, setShipmentType] = useState(
    "Freight to Pay by Customer"
  );
  const [orderDetailsModal, setOrderDetailsModal] = useState(false);
  const today = new Date().toISOString().split("T")[0];
  const [orderDetails, setOrderDetails] = useState({
    orderType: "Normal Order",
    requestedDate: today,
    project_type: "Private",
    project_segment: null,
    doc_number: null,
    note: null,
  });

  const [displayedProducts, setDisplayedProducts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [totalProducts, setTotalProducts] = useState(0); // NEW - Added this
  const [searchQuery, setSearchQuery] = useState("");

  const dropdownRef = useRef(null);
  const ITEMS_PER_PAGE = 20;

  // useEffect(() => {
  //   dispatch(AllProductsAPI({ dealer_code })).catch((error) => {
  //     console.error("Error fetching the products;", error);
  //   });
  // }, [dispatch, dealer_code]);

  useEffect(() => {
    if (dealer_code) {
      setDisplayedProducts([]);
      setCurrentPage(1);
      setHasMore(true);

      dispatch(
        AllProductsAPI({
          dealer_code: dealer_code,
          page: 1,
          per_page: ITEMS_PER_PAGE,
        })
      )
        .then((response) => {
          console.log(
            "response AllProductsAPI ============",
            response.payload.data.meta
          );
          const products = response?.payload?.data.data || [];
          const total = response?.payload?.data?.meta?.total || 0;

          setDisplayedProducts(products);
          setTotalProducts(total);
          setHasMore(products.length < total);
        })
        .catch((error) => {
          console.error("Error fetching the products:", error);
          setHasMore(false);
        });
    }
  }, [dispatch]);

  const handleSearchChange = (value) => {
    setSearchQuery(value);

    if (dealer_code) {
      setDisplayedProducts([]);
      setCurrentPage(1);
      setHasMore(true);
      setIsLoadingMore(true);

      dispatch(
        AllProductsAPI({
          dealer_code: dealer_code,
          page: 1,
          per_page: ITEMS_PER_PAGE,
          search: value, // Send search query
        })
      )
        .then((response) => {
          const products = response?.payload?.data?.data || [];
          const total = response?.payload?.data?.meta?.total || 0;

          setDisplayedProducts(products);
          setTotalProducts(total);
          setHasMore(products.length < total);
          setIsLoadingMore(false);
        })
        .catch((error) => {
          console.error("Error fetching products:", error);
          setHasMore(false);
          setIsLoadingMore(false);
        });
    }
  };

  const loadMoreProducts = () => {
    if (isLoadingMore || !hasMore) return;

    setIsLoadingMore(true);
    const nextPage = currentPage + 1;

    dispatch(
      AllProductsAPI({
        dealer_code: dealer_code,
        page: nextPage,
        per_page: ITEMS_PER_PAGE,
        search: searchQuery, // Include search in pagination
      })
    )
      .then((response) => {
        const newProducts = response?.payload?.data?.data || [];
        const total = response?.payload?.data?.meta?.total || 0;

        if (newProducts.length > 0) {
          setDisplayedProducts((prev) => {
            const updated = [...prev, ...newProducts];
            const totalLoaded = updated.length;
            setHasMore(totalLoaded < total);
            return updated;
          });

          setCurrentPage(nextPage);
          setTotalProducts(total);
        } else {
          setHasMore(false);
        }

        setIsLoadingMore(false);
      })
      .catch((error) => {
        console.error("Error loading more products:", error);
        setHasMore(false);
        setIsLoadingMore(false);
      });
  };

  const handleDropdownVisibleChange = (open) => {
    if (open && displayedProducts.length === 0) {
      // Load initial products if not already loaded
      if (dealer_code) {
        dispatch(
          AllProductsAPI({
            dealer_code: dealer_code,
            page: 1,
            per_page: ITEMS_PER_PAGE,
          })
        )
          .then((response) => {
            // Fixed: API returns data directly, not data.data
            const products = response?.payload?.data?.data || [];
            // Fixed: Total is in meta.total, not data.total
            const total = response?.payload?.meta?.total || 0;

            setDisplayedProducts(products);
            setCurrentPage(1);
            setTotalProducts(total);
            setHasMore(products.length < total);
          })
          .catch((error) => {
            console.error("Error fetching products:", error);
          });
      }
    }
  };
  const handlePopupScroll = (e) => {
    const { target } = e;
    const threshold = 10;

    if (
      target.scrollTop + target.offsetHeight >=
      target.scrollHeight - threshold
    ) {
      loadMoreProducts();
    }
  };

  const [cartItems, setCartItems] = useState([]);
  const [qtyDraft, setQtyDraft] = useState({}); // <- draft text per SKU while typing

  const formatINR = (n) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(n || 0);

  useEffect(() => {
    document.title = "Cart: ALP Shop";
    if (!authData.is_Login || !dealer_code) {
      dispatch(logout());
      setLocation("/");
    }
  }, [authData.is_Login, setLocation, dealer_code, dispatch]);

  const addToCartFromSelect = async (erp_item_code) => {
    if (!dealer_code) {
      console.warn("No dealer_code found in auth_store");
      return;
    }

    try {
      const res = await dispatch(PDetailsPrice({ erp_item_code, dealer_code }));
      const productDetails = res?.payload?.data?.data;
      if (!productDetails) return;

      const packQty =
        Number(productDetails.product.packaging_quantity_wholesaler_box) || 0;
      const unitPrice = Number(productDetails.pricing.unit_price) || 0;
      const boxPrice = unitPrice * packQty;
      const gst = Number(productDetails.product.gst) || 0;
      const qty = 1;
      const discount = 0; // %
      const total_price = boxPrice * qty;
      const pad = Number(
        (total_price - (discount * total_price) / 100).toFixed(5)
      );
      const gstAmount = Number(((gst * pad) / 100).toFixed(5));
      const finalPrice = Number((pad + gstAmount).toFixed(5));

      const newRow = {
        sku: productDetails.product.erp_item_code,
        name: `${productDetails.product.title}-${productDetails.product.erp_item_code}`,
        packQty,
        unitPrice,
        boxPrice: Number(boxPrice.toFixed(5)),
        qty,
        gst,
        gstAmount,
        discount,
        pad,
        finalPrice,
        msp_price: productDetails?.pricing?.msp_price,
        total_price: total_price,
      };

      setCartItems((prev) => {
        const i = prev.findIndex((p) => p.sku === newRow.sku);
        if (i > -1) {
          const copy = [...prev];
          const existing = copy[i];
          const qty2 = existing.qty + 1;
          const total_price2 = existing.boxPrice * qty2;
          const pad2 = Number(
            (total_price2 - (existing.discount * total_price2) / 100).toFixed(5)
          );
          const gstAmount2 = Number(((existing.gst * pad2) / 100).toFixed(5));
          const finalPrice2 = Number((pad2 + gstAmount2).toFixed(5));
          copy[i] = {
            ...existing,
            qty: qty2,
            pad: pad2,
            gstAmount: gstAmount2,
            finalPrice: finalPrice2,
            msp_price: productDetails?.pricing?.msp_price,
            total_price: total_price2,
          };
          return copy;
        }
        return [...prev, newRow];
      });
    } catch (e) {
      console.error("Error fetching product details:", e);
    }
  };

  // ------- helpers for qty & totals (fixes backspace issue)
  function recalc(it, qty) {
    const total_price = it.boxPrice * qty;
    const pad = Number(
      (total_price - (it.discount * total_price) / 100).toFixed(5)
    );
    const gstAmount = Number(((it.gst * pad) / 100).toFixed(5));
    const finalPrice = Number((pad + gstAmount).toFixed(5));
    return { total_price, pad, gstAmount, finalPrice };
  }

  function onQtyChange(sku, str) {
    // allow user to type freely (including empty)
    setQtyDraft((prev) => ({ ...prev, [sku]: str }));
  }

  function onQtyBlur(sku, str) {
    const qty = Math.max(1, Number(str) || 1);
    setCartItems((prev) =>
      prev.map((it) => {
        if (it.sku !== sku) return it;
        const { total_price, pad, gstAmount, finalPrice } = recalc(it, qty);
        return { ...it, qty, total_price, pad, gstAmount, finalPrice };
      })
    );
    // clear draft after committing
    setQtyDraft((prev) => {
      const { [sku]: _, ...rest } = prev;
      return rest;
    });
  }
  // ------- end qty helpers

  const removeRow = (sku) => {
    setCartItems((prev) => prev.filter((it) => it.sku !== sku));
    // also clear any draft for this row
    setQtyDraft((prev) => {
      const { [sku]: _, ...rest } = prev;
      return rest;
    });
  };

  const subTotal = cartItems.reduce((s, it) => s + (it.pad || 0), 0);
  const totalGST = cartItems.reduce((s, it) => s + (it.gstAmount || 0), 0);
  const grandTotal = subTotal + totalGST;

  const saveTimer = useRef();

  // Load cart from backend on mount/dealer change
  useEffect(() => {
    dispatch(GetDealerCart({ dealerCode: dealer_code })).then((response) => {
      const products = response?.payload?.data?.data?.details?.products;
      if (products) {
        setCartItems(
          products.map((items) => {
            return {
              pad: items?.pad,
              sku: items?.alp_code,
              name: items?.title,
              packQty: items?.packaging_quantity_wholesaler_box,
              unitPrice: items?.unit_price,
              boxPrice: items?.boxPrice,
              qty: items?.quantity,
              discount: items?.discount ?? 0,
              total_price: items?.total_price,
              gst: items?.gst,
              gstAmount: items?.gstNumber,
              finalPrice: items?.final_price,
              msp_price: items?.msp,
            };
          })
        );
        setQtyDraft({}); // reset drafts after loading
      } else {
        setCartItems([]);
        setQtyDraft({});
      }
    });
  }, [setCartItems, dealer_code, dispatch]);

  // Debounced autosave cart to backend
  useEffect(() => {
    clearTimeout(saveTimer.current);
    if (cartItems.length === 0) {
      const payload = {
        dealerCode: dealer_code,
        details: {
          products: [],
          subTotal: 0,
          totalGST: 0,
          grandTotal: 0,
        },
      };
      dispatch(DealerSaveToCart(payload)).catch(console.error);
    } else {
      saveTimer.current = setTimeout(() => {
        const payload = {
          dealerCode: dealer_code,
          details: {
            products: cartItems.map((it) => ({
              pad: it.pad,
              alp_code: it.sku,
              title: it.name,
              packaging_quantity_wholesaler_box: it.packQty,
              unit_price: it.unitPrice,
              boxPrice: it.boxPrice,
              quantity: it.qty,
              discount: it.discount ?? 0,
              total_price: it.total_price,
              gst: it.gst,
              gstNumber: it.gstAmount,
              final_price: it.finalPrice,
              msp: it.msp_price,
            })),
            subTotal,
            totalGST,
            grandTotal,
          },
        };
        dispatch(DealerSaveToCart(payload)).catch(console.error);
      }, 800);
    }

    return () => clearTimeout(saveTimer.current);
  }, [cartItems, dealer_code, subTotal, totalGST, grandTotal, dispatch]);

  return (
    <div className="sm:flex sm:p-10 max-sm:p-4 gap-5">
      <div className="sm:w-2/3 bg-gray-50 rounded-lg p-4 shadow-lg">
        <div className="sm:flex justify-between items-center">
          <Title level={2}>Cart</Title>
          <ChangeDealerCode />
        </div>

        {cartItems.length < 1 ? (
          <div className="text-center mt-8">
            <p className="text-lg font-semibold">Your cart is empty</p>
            <p className="text-gray-500">
              Add products to your cart to place an order
            </p>
          </div>
        ) : (
          <>
            <ConfigProvider
              theme={{
                components: {
                  Typography: { titleMarginTop: 0, titleMarginBottom: 0 },
                },
              }}
            >
              <div className="max-sm:hidden flex max-w-full border-b border-t">
                <div className="w-[15%] py-2 text-center border-s border-r">
                  <Title level={4}>SKU</Title>
                </div>
                <div className="w-[53%] py-2 text-center border-r">
                  <Title level={4}>Product Name</Title>
                </div>
                <div className="w-[12%] py-2 text-center border-r">
                  <Title level={4}>Quantity</Title>
                </div>
                <div className="w-[15%] py-2 text-center border-r">
                  <Title level={4}>Total Price</Title>
                </div>
                <div className="w-[5%] py-2 border-r"></div>
              </div>
            </ConfigProvider>

            {/* Cart Rows of added Products */}
            {cartItems.map((it) => (
              <div
                key={it.sku}
                className="max-sm:relative flex max-w-full border-b form-list w-full max-sm:flex-wrap max-sm:my-4 max-sm:bg-blue-100 max-sm:p-4 max-sm:rounded-lg max-sm:items-start"
              >
                <div className="max-sm:w-[40%] sm:w-[15%] sm:grid place-items-center sm:text-center max-sm:text-left sm:border-s sm:border-r font-medium uppercase">
                  {it.sku}
                </div>
                <div className="sm:p-2 max-sm:w-[60%] sm:w-[53%] text-left sm:border-r max-sm:pe-[10px]">
                  <div className="font-medium">
                    {it.name} - Pack of: {it.packQty}
                  </div>
                </div>
                <div className="sm:grid sm:px-2 place-items-center sm:w-[12%] text-left sm:border-r max-sm:border-t max-sm:border-b max-sm:border-gray-500 max-sm:w-full max-sm:py-2 max-sm:my-2 max-sm:flex justify-between max-sm:items-center">
                  <div className="sm:hidden font-semibold w-2/3">Quantity</div>
                  <Input
                    type="number"
                    min={1}
                    value={qtyDraft[it.sku] ?? String(it.qty)}
                    onChange={(e) => onQtyChange(it.sku, e.target.value)}
                    onBlur={(e) => onQtyBlur(it.sku, e.target.value)}
                    className="max-sm:w-[45%] text-center"
                  />
                </div>
                <div className="flex items-center sm:w-[15%] sm:text-center sm:border-r max-sm:w-full justify-between">
                  <div className="sm:hidden font-semibold w-2/3 flex">
                    Total Price
                  </div>
                  <p className="sm:text-center sm:w-full">
                    {formatINR(it.finalPrice)}
                  </p>
                </div>
                <div className="text-center sm:w-[5%] sm:border-r justify-center items-center flex max-sm:absolute max-sm:right-2 max-sm:top-2">
                  <div onClick={() => removeRow(it.sku)}>
                    <CloseSquareOutlined className="text-[#B91C1C] max-sm:text-xl" />
                  </div>
                </div>
              </div>
            ))}
          </>
        )}

        <div className="mt-6 text-left">
          <Title level={4}>Add products to Cart</Title>
          <div className="flex flex-col gap-4 sm:w-2/3">
            {/* <Select
              showSearch
              value={null}
              placeholder="Select Product"
              onChange={(value) => addToCartFromSelect(value)}
              filterOption={(input, option) =>
                option?.name?.toLowerCase().includes(input.toLowerCase()) ||
                option?.value?.toLowerCase().includes(input.toLowerCase())
              }
              notFoundContent={
                all_products_options.length < 1 ? (
                  <div className="flex justify-center p-2">
                    <Spin size="small" />
                  </div>
                ) : null
              }
              options={all_products_options.map((items) => ({
                key: `${items.erp_item_code}`,
                value: `${items.erp_item_code}`,
                name: items.name,
                label: (
                  <div
                    className={`flex justify-between p-2 ${
                      items.is_expired === 1
                        ? "bg-gray-100 ant-tag ant-tag-volcano max-sm:text-red-500"
                        : ""
                    }`}
                  >
                    <div className="flex">
                      {items.name}, {items.erp_item_code.toUpperCase()}
                    </div>
                    {items.is_expired === 1 && (
                      <div
                        className="ant-tag ant-tag-volcano m-0"
                        style={{ color: "red" }}
                      >
                        Expired
                      </div>
                    )}
                  </div>
                ),
                disabled: items.is_expired === 1,
              }))}
            /> */}
            <Select
              showSearch
              placeholder="Select Product"
              onChange={(value) => addToCartFromSelect(value)}
              onSearch={handleSearchChange} // NEW: Call handleSearchChange whetyping
              onDropdownVisibleChange={handleDropdownVisibleChange}
              onPopupScroll={handlePopupScroll}
              filterOption={(input, option) =>
                option?.name?.toLowerCase().includes(input.toLowerCase()) ||
                option?.value?.toLowerCase().includes(input.toLowerCase())
              }
              dropdownRender={(menu) => (
                <>
                  {menu}
                  {isLoadingMore && (
                    <div
                      style={{
                        textAlign: "center",
                        padding: "8px",
                        borderTop: "1px solid #f0f0f0",
                      }}
                    >
                      <Spin
                        indicator={
                          <LoadingOutlined style={{ fontSize: 24 }} spin />
                        }
                      />
                    </div>
                  )}
                  {!hasMore && displayedProducts.length > 0 && (
                    <div
                      style={{
                        textAlign: "center",
                        padding: "8px",
                        color: "#999",
                        fontSize: "12px",
                      }}
                    >
                      No more products
                    </div>
                  )}
                </>
              )}
              options={
                Array.isArray(displayedProducts)
                  ? displayedProducts.map((items) => ({
                      key: `${items.erp_item_code}`,
                      value: `${items.erp_item_code}`,
                      name: items.name,
                      label: (
                        <div
                          className={`flex justify-between p-2 ${
                            items.is_expired === 1 ? "bg-gray-100" : ""
                          }`}
                        >
                          <div className="flex">
                            {items.name}, {items.erp_item_code.toUpperCase()}
                          </div>
                          {items.is_expired === 1 && (
                            <div
                              className="ant-tag ant-tag-volcano css-dev-only-do-not-override-1oemflq m-0"
                              style={{ color: "red" }}
                            >
                              Expired
                            </div>
                          )}
                        </div>
                      ),
                      disabled: items.is_expired === 1,
                    }))
                  : []
              }
            />
          </div>
        </div>
      </div>

      <div className="sm:w-1/3 text-left">
        <ShippingAddresses
          dealer_code={dealer_code}
          setLocation={setLocation}
          setBalanceCredit={setBalanceCredit}
          defaultShippingAddress={defaultShippingAddress}
          setDefaultShippingAddress={setDefaultShippingAddress}
        />
        {/* Totals */}
        <div className="bg-gray-50 rounded-lg p-4 shadow-lg mt-8">
          {cartItems.length > 0 && (
            <>
              <div className="flex items-center justify-between">
                <p>Sub Total</p>
                <p>{formatINR(subTotal)}</p>
              </div>
              <div className="flex items-center justify-between">
                <p>Total GST</p>
                <p>{formatINR(totalGST)}</p>
              </div>
              <div className="flex items-center justify-between border-b border-t my-2 py-2 text-lg font-semibold">
                <p>Grand Total</p>
                <p>{formatINR(grandTotal)}</p>
              </div>
            </>
          )}
          <div className="font-semibold">
            Balanced Credit Limit: {formatINR(balanceCredit)}
          </div>
          <div className="my-2 border-t py-2 flex flex-wrap items-center">
            <p className="font-medium text-lg w-1/3">Shipment</p>
            <Radio.Group
              value={shipmentType}
              onChange={(e) => setShipmentType(e.target.value)}
              options={[
                {
                  label: "Freight Billed in Invoice",
                  value: "Freight Added to Invoice",
                },
                { label: "Freight Paid", value: "Freight As per Customer PO" },
                { label: "To Pay", value: "Freight to Pay by Customer" },
              ]}
            />
          </div>

          {shipmentType === "Freight Added to Invoice" && (
            <p className="border-b pb-2">
              ALP will handle shipment to your address. Additional charges for
              shipping will apply. You will receive final payment link on email.
            </p>
          )}
          {shipmentType === "Freight As per Customer PO" && (
            <p className="border-b pb-2">
              You agree to pickup these goofs from ALP Aeroflex. You will
              receive order confirmation on email. It's for self pickup.
            </p>
          )}
          {shipmentType === "Freight to Pay by Customer" && (
            <p className="border-b pb-2">
              ALP Shop will manage your shipment, and you'll handle payment
              directly to the trucking company.
            </p>
          )}
          <div>
            <Button
              type="primary"
              className="bg-[#B91C1C] rounded-lg w-full mt-4"
              disabled={grandTotal > balanceCredit || cartItems.length < 1}
              onClick={() => setOrderDetailsModal(true)}
            >
              PLACE ORDER REQUEST
            </Button>
          </div>
        </div>
      </div>

      <OrderDetailsModal
        dispatch={dispatch}
        email={authData.email_or_mobile}
        today={today}
        dealer_code={dealer_code}
        authData={authData}
        orderDetailsModal={orderDetailsModal}
        setOrderDetailsModal={setOrderDetailsModal}
        orderDetails={orderDetails}
        setOrderDetails={setOrderDetails}
        shippingDetails={defaultShippingAddress}
        balanceCredit={balanceCredit}
        cartItems={cartItems}
        setCartItems={setCartItems}
        shipmentType={shipmentType}
        subTotal={subTotal}
        totalGST={totalGST}
        grandTotal={grandTotal}
        setLocation={setLocation}
      />
    </div>
  );
};

export default Cart;
